/**
 * Sphere-constrained layout optimizer.
 *
 * Run BEFORE handing positions to the d3-force-3d worker, in a synchronous
 * main-thread pass. Minimizes a stress function similar to a Fruchterman-
 * Reingold layout but projects each node back to its target shell after
 * every step, so the radial structure is preserved exactly while angular
 * placement settles into something that minimizes total edge length.
 *
 * Why this exists: the worker's radial force is intentionally strong (0.85+)
 * to keep nodes locked to their shells. As a side effect, its link force is
 * weak (0.12), which means the sim doesn't have many tangential cycles to
 * move things into good angular positions before cooling. Pre-optimizing
 * the angular layout lets the worker start from a near-optimal state and
 * just do light refinement.
 *
 * Complexity: O(iterations * N^2). At MVP scale (~46 nodes, 80 iters)
 * that's ~170k ops — submillisecond. Scales fine to a few hundred nodes.
 */

export type OptNode = {
  id: string;
  x: number;
  y: number;
  z: number;
  pinned?: boolean;
  targetRadius: number;
  /**
   * Optional cluster key. Same-cluster nodes get an additional attractive
   * pull toward their cluster centroid. Null/undefined = no cluster
   * affinity (existing topology-only behavior).
   */
  cluster?: string | null;

  /**
   * Optional finer key applied inside the primary cluster. Same force,
   * weaker pull — see subClusterIdFor() for why the key is composed
   * rather than bare.
   */
  subCluster?: string | null;
};

export type OptEdge = {
  source: string;
  target: string;
  weight: number;
};

type Params = {
  iterations?: number;
  attract?: number;
  repel?: number;
  /**
   * Strength of the same-cluster centroid pull. 0 disables (default
   * behavior). Tuned so cluster pull is comparable to but doesn't dominate
   * edge attraction.
   */
  cluster?: number;

  /** Strength of the sub-cluster pull. 0 disables. */
  subCluster?: number;
  stepDecay?: 'linear' | 'sqrt';
};

const DEFAULTS: Required<Params> = {
  iterations: 80,
  attract: 0.9,
  repel: 1.6,
  cluster: 0,
  subCluster: 0,
  stepDecay: 'linear',
};

export function optimizeOnSphere(
  nodes: OptNode[],
  edges: OptEdge[],
  params: Params = {},
): void {
  const { iterations, attract, repel, cluster, subCluster, stepDecay } = { ...DEFAULTS, ...params };

  const idx = new Map<string, number>();
  nodes.forEach((n, i) => idx.set(n.id, i));

  // Adjacency list with edge weights, used for the attractive term.
  const adj: Array<Array<{ ni: number; w: number }>> = nodes.map(() => []);
  for (const e of edges) {
    const si = idx.get(e.source);
    const ti = idx.get(e.target);
    if (si === undefined || ti === undefined) continue;
    adj[si].push({ ni: ti, w: e.weight });
    adj[ti].push({ ni: si, w: e.weight });
  }

  // Cluster membership index: cluster key -> [nodeIndex, ...].
  // Only built when cluster force is enabled to avoid wasted allocation.
  const clusterMembers = new Map<string, number[]>();
  const subClusterMembers = new Map<string, number[]>();
  if (subCluster > 0) {
    nodes.forEach((n, i) => {
      if (!n.subCluster) return;
      const list = subClusterMembers.get(n.subCluster);
      if (list) list.push(i);
      else subClusterMembers.set(n.subCluster, [i]);
    });
  }
  if (cluster > 0) {
    nodes.forEach((n, i) => {
      if (!n.cluster) return;
      const list = clusterMembers.get(n.cluster);
      if (list) list.push(i);
      else clusterMembers.set(n.cluster, [i]);
    });
  }

  // Scratch force accumulator.
  const fx = new Float64Array(nodes.length);
  const fy = new Float64Array(nodes.length);
  const fz = new Float64Array(nodes.length);

  for (let iter = 0; iter < iterations; iter++) {
    const t = iter / iterations;
    // Step magnitude tapers — large early to escape bad seeds, small late
    // to fine-tune.
    const decay = stepDecay === 'sqrt' ? 1 - Math.sqrt(t) : 1 - t;
    const step = 0.5 * decay;

    fx.fill(0); fy.fill(0); fz.fill(0);

    // Pairwise repulsion. Inverse-square law softened to avoid blowing up
    // when two nodes happen to overlap.
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].pinned) continue;
      for (let j = i + 1; j < nodes.length; j++) {
        if (nodes[j].pinned) continue;
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const dz = nodes[i].z - nodes[j].z;
        const d2 = Math.max(0.5, dx * dx + dy * dy + dz * dz);
        const f = repel / d2;
        const d = Math.sqrt(d2);
        const ux = dx / d, uy = dy / d, uz = dz / d;
        fx[i] += ux * f; fy[i] += uy * f; fz[i] += uz * f;
        fx[j] -= ux * f; fy[j] -= uy * f; fz[j] -= uz * f;
      }
    }

    // Edge-driven attraction. Connected nodes pulled toward each other,
    // weighted by edge weight so popular moves group harder.
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].pinned) continue;
      for (const { ni, w } of adj[i]) {
        const dx = nodes[ni].x - nodes[i].x;
        const dy = nodes[ni].y - nodes[i].y;
        const dz = nodes[ni].z - nodes[i].z;
        const d = Math.hypot(dx, dy, dz) || 1;
        const f = attract * w * d * 0.05; // proportional to distance
        fx[i] += (dx / d) * f;
        fy[i] += (dy / d) * f;
        fz[i] += (dz / d) * f;
      }
    }

    // Centroid pull, run once per grouping level. For each group,
    // compute the live centroid and add an attractive force on each
    // member toward it. Strength scales with distance so already-grouped
    // nodes don't get unnecessary nudges while stragglers get pulled in.
    //
    // Two levels, same force, different strengths. The primary gathers a
    // third of the map into a region; the sub level knots families inside
    // it. Running the weaker one second means a node feels the coarse
    // pull first and the fine one as a correction, which is the tree
    // shape rather than two forces arguing.
    for (const [members, strength] of [
      [clusterMembers, cluster] as const,
      [subClusterMembers, subCluster] as const,
    ]) {
      if (strength <= 0 || members.size === 0) continue;
      for (const memberIndices of members.values()) {
        if (memberIndices.length < 2) continue;
        let cx = 0, cy = 0, cz = 0;
        for (const mi of memberIndices) {
          cx += nodes[mi].x;
          cy += nodes[mi].y;
          cz += nodes[mi].z;
        }
        const inv = 1 / memberIndices.length;
        cx *= inv; cy *= inv; cz *= inv;
        for (const mi of memberIndices) {
          if (nodes[mi].pinned) continue;
          const dx = cx - nodes[mi].x;
          const dy = cy - nodes[mi].y;
          const dz = cz - nodes[mi].z;
          const d = Math.hypot(dx, dy, dz) || 1;
          // Same proportional-to-distance treatment as edge attraction so
          // the dynamics stay comparable.
          const f = strength * d * 0.05;
          fx[mi] += (dx / d) * f;
          fy[mi] += (dy / d) * f;
          fz[mi] += (dz / d) * f;
        }
      }
    }

    // Apply forces and project back to each node's target shell. Pinned
    // nodes are skipped entirely so the center stays at origin.
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      if (n.pinned) continue;
      n.x += fx[i] * step;
      n.y += fy[i] * step;
      n.z += fz[i] * step;
      const r = Math.hypot(n.x, n.y, n.z) || 1;
      const k = n.targetRadius / r;
      n.x *= k; n.y *= k; n.z *= k;
    }
  }
}
